import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FaTruck, FaEdit, FaSave, FaTimes, FaExclamationTriangle,
  FaGasPump, FaArrowLeft, FaArrowDown, FaArrowUp, FaWrench,
  FaMapMarkerAlt, FaSync, FaCalendarAlt, FaTimesCircle, FaClipboardCheck, FaUnlock,
} from 'react-icons/fa';
import { useModal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { useApiCall } from '../hooks/useApiCall';
import '../styles/FuelLevels.css';
import '../styles/Statistics.css';

// ── Custom date input (matches Statistics picker style) ───────────────────────
const DateInput = React.forwardRef(({ value, onClick }, ref) => (
  <button type="button" className="stat-date-input" onClick={onClick} ref={ref}>
    <FaCalendarAlt className="stat-date-input-icon" />
    <span>{value}</span>
  </button>
));
DateInput.displayName = 'DateInput';

// ── Helpers ────────────────────────────────────────────────────────────────────

function toTs(date) {
  return Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0).getTime() / 1000);
}
function toEndTs(date) {
  return Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).getTime() / 1000);
}
function daysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n); d.setHours(0, 0, 0, 0); return d;
}
function fmtTs(ts) {
  return new Date(ts * 1000).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
}
function fmtNum(n) {
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

// ── Edit fuel level modal form ─────────────────────────────────────────────────

function EditFuelForm({ tanker, onSave, closeModal }) {
  const [value, setValue] = useState(String(tanker.amount));
  const [reason, setReason] = useState('');
  const { showToast } = useToast();

  const handleSubmit = (e) => {
    e.preventDefault();
    const num = parseFloat(value);
    if (isNaN(num) || num < 0 || num > tanker.amount_cap) {
      showToast(`Value must be between 0 and ${tanker.amount_cap}`, 'error');
      return;
    }
    onSave(tanker.id, num, reason);
    closeModal();
  };

  const pct = Math.round((parseFloat(value) / tanker.amount_cap) * 100) || 0;
  const barColor = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';

  return (
    <form id="fuel-form" onSubmit={handleSubmit} className="edit-fuel-form">
      <div className="form-row">
        <label className="form-label">Tanker</label>
        <div className="form-static">{tanker.name} - {tanker.fuelType}</div>
      </div>
      <div className="form-row">
        <label className="form-label">Capacity</label>
        <div className="form-static">{tanker.amount_cap} {tanker.amount_type}</div>
      </div>
      <div className="form-row">
        <label className="form-label">New level ({tanker.amount_type})</label>
        <input
          type="number"
          className="form-input"
          value={value}
          onChange={e => setValue(e.target.value)}
          min={0}
          max={tanker.amount_cap}
          step={0.1}
          required
        />
      </div>
      <div className="fuel-preview">
        <div className="fuel-bar-track">
          <div className="fuel-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
        </div>
        <span style={{ color: barColor }}>{pct}%</span>
      </div>
      <div className="form-row">
        <label className="form-label">Reason / Note</label>
        <input
          type="text"
          className="form-input"
          placeholder="e.g. Manual correction after inspection"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      </div>
    </form>
  );
}

// ── Daily bar chart (pure CSS) ─────────────────────────────────────────────────

function DailyChart({ daily, unit }) {
  if (!daily?.length) return <div className="fl-empty-chart">No activity in this period.</div>;
  const maxOut = Math.max(...daily.map(d => d.out_volume), 1);
  return (
    <div className="daily-chart">
      {daily.map(d => (
        <div key={d.day} className="daily-bar-col">
          <div className="daily-bar-wrap">
            {d.in_volume > 0 && (
              <div
                className="daily-bar daily-bar-in"
                style={{ height: `${Math.round((d.in_volume / maxOut) * 100)}%` }}
                title={`IN: ${fmtNum(d.in_volume)} ${unit}`}
              />
            )}
            <div
              className="daily-bar daily-bar-out"
              style={{ height: `${Math.round((d.out_volume / maxOut) * 100)}%` }}
              title={`OUT: ${fmtNum(d.out_volume)} ${unit}`}
            />
          </div>
          <div className="daily-bar-label">{d.day.slice(5)}</div>
        </div>
      ))}
      <div className="daily-chart-legend">
        <span className="dcl-dot out" /> OUT
        <span className="dcl-dot in" /> IN
      </div>
    </div>
  );
}

// ── Tanker detail panel ────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

function TankerDetail({ tanker: initialTanker, onBack, onCorrect, onForceUnlock }) {
  const apiCall = useApiCall();

  const [from, setFrom] = useState(daysAgo(30));
  const [to,   setTo]   = useState(new Date());
  const [page, setPage] = useState(1);

  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshLocked, setRefreshLocked] = useState(false);

  // live tanker (refreshed after corrections)
  const [tanker, setTanker] = useState(initialTanker);

  const load = useCallback((fromDate, toDate, p) => {
    setLoading(true);
    apiCall('admin/tankers/detail', 'POST', {
      tanker_id: tanker.id,
      from_ts:   toTs(fromDate),
      to_ts:     toEndTs(toDate),
      page:      p,
    }).then(res => {
      if (res?.success) {
        setDetail(res.data);
        setTanker(res.data.tanker); // keep fuel level fresh
      }
      setLoading(false);
    });
  }, [apiCall, tanker.id]);

  useEffect(() => { load(from, to, 1); setPage(1); }, [from, to]); // eslint-disable-line

  const handlePage = (p) => { setPage(p); load(from, to, p); };

  const handleRefresh = () => {
    if (refreshLocked) return;
    setRefreshLocked(true);
    load(from, to, page);
    setTimeout(() => setRefreshLocked(false), 5000);
  };

  const quickRange = (days) => {
    const f = daysAgo(days);
    const t = new Date();
    setFrom(f); setTo(t); setPage(1);
  };

  const stats    = detail?.stats    ?? {};
  const daily    = detail?.daily    ?? [];
  const txns     = detail?.transactions ?? [];
  const total    = detail?.total    ?? 0;
  const pages    = Math.ceil(total / PAGE_SIZE);

  const pct      = tanker.level_pct ?? 0;
  const barColor = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';

  return (
    <div className="fl-detail">
      {/* Header */}
      <div className="fl-detail-header">
        <button className="btn btn-secondary btn-sm" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
        <div className="fl-detail-title">
          <FaTruck /> {tanker.name}
          <span className={`tanker-status-badge ${tanker.in_use ? 'in-use' : 'available'}`}>
            {tanker.in_use ? 'In Use' : 'Available'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={handleRefresh} disabled={refreshLocked}>
            <FaSync className={refreshLocked ? 'icon-spin' : ''} /> {refreshLocked ? 'Refreshing…' : 'Refresh'}
          </button>
          {tanker.in_use ? (
            <button className="btn btn-sm btn-danger" onClick={() => onForceUnlock(tanker)}>
              <FaUnlock /> Force unlock
            </button>
          ) : null}
          <button className="btn btn-primary btn-sm" onClick={() => onCorrect(tanker)}>
            <FaEdit /> Correct level
          </button>
        </div>
      </div>

      {/* Tanker summary card */}
      <div className="fl-detail-summary">
        <div className="fl-ds-left">
          <div className="fl-fuel-type"><FaGasPump /> {tanker.fuelType}</div>
          {tanker.location && (
            <div className="fl-fuel-type"><FaMapMarkerAlt /> {tanker.location}</div>
          )}
        </div>
        <div className="fl-ds-right">
          <div className="fl-bar-section">
            <div className="fuel-bar-track large">
              <div className="fuel-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
            </div>
            <div className="fl-bar-labels">
              <span style={{ color: barColor, fontWeight: 700, fontSize: 20 }}>{pct}%</span>
              <span className="fl-gal">{fmtNum(tanker.amount)} / {fmtNum(tanker.amount_cap)} {tanker.amount_type}</span>
            </div>
          </div>
          {pct < 20 && (
            <div className="fl-warning"><FaExclamationTriangle /> Low fuel - refill required</div>
          )}
          {tanker.bowser_check === 'fail' && (
            <div className="fl-warning fl-bowser-fail"><FaTimesCircle /> Bowser check failed</div>
          )}
          {tanker.bowser_check === 'not_checked' && (
            <div className="fl-warning fl-bowser-none"><FaClipboardCheck /> No bowser check today</div>
          )}
        </div>
      </div>

      {/* Date range controls */}
      <div className="fl-detail-range">
        <div className="fl-range-quick">
          {[[7,'7d'],[30,'30d'],[90,'90d']].map(([d, label]) => (
            <button key={d} className="btn btn-sm btn-secondary" onClick={() => quickRange(d)}>{label}</button>
          ))}
        </div>
        <div className="fl-range-pickers">
          <DatePicker
            selected={from}
            onChange={d => d && setFrom(d)}
            selectsStart startDate={from} endDate={to}
            maxDate={to}
            dateFormat="MM/dd/yyyy"
            customInput={<DateInput />}
            popperClassName="stat-datepicker-popper"
            calendarClassName="stat-datepicker"
            popperProps={{ strategy: 'fixed' }}
          />
          <span className="fl-range-sep">→</span>
          <DatePicker
            selected={to}
            onChange={d => d && setTo(d)}
            selectsEnd startDate={from} endDate={to}
            minDate={from} maxDate={new Date()}
            dateFormat="MM/dd/yyyy"
            customInput={<DateInput />}
            popperClassName="stat-datepicker-popper"
            calendarClassName="stat-datepicker"
            popperProps={{ strategy: 'fixed' }}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading...</div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="fl-stat-grid">
            <div className="fl-stat-card out">
              <div className="fl-stat-label"><FaArrowUp /> Total OUT</div>
              <div className="fl-stat-val">{fmtNum(stats.total_out ?? 0)}</div>
              <div className="fl-stat-sub">{tanker.amount_type} · {stats.out_count ?? 0} ops</div>
            </div>
            <div className="fl-stat-card in">
              <div className="fl-stat-label"><FaArrowDown /> Total IN</div>
              <div className="fl-stat-val">{fmtNum(stats.total_in ?? 0)}</div>
              <div className="fl-stat-sub">{tanker.amount_type} · {stats.in_count ?? 0} loads</div>
            </div>
            <div className="fl-stat-card neutral">
              <div className="fl-stat-label">Transactions</div>
              <div className="fl-stat-val">{stats.total_txns ?? 0}</div>
              <div className="fl-stat-sub">total in period</div>
            </div>
            <div className="fl-stat-card correction">
              <div className="fl-stat-label"><FaWrench /> Corrections</div>
              <div className="fl-stat-val">{stats.correction_count ?? 0}</div>
              <div className="fl-stat-sub">manual adjustments</div>
            </div>
          </div>

          {/* Daily chart */}
          <div className="fl-detail-section">
            <div className="fl-section-title">Daily Activity</div>
            <DailyChart daily={daily} unit={tanker.amount_type} />
          </div>

          {/* Transaction table */}
          <div className="fl-detail-section">
            <div className="fl-section-title">
              Transactions
              <span className="fl-txn-count">{total} total</span>
            </div>
            <div className="users-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th>Aircraft / Vendor / Notes</th>
                    <th>Fueler / Admin</th>
                    <th>Amount</th>
                    <th>Date / Time</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.length === 0 ? (
                    <tr><td colSpan={6} className="table-empty">No transactions in this period.</td></tr>
                  ) : txns.map(tx => (
                    <tr key={tx.id}>
                      <td className="txn-id">{tx.id}</td>
                      <td>
                        {tx.type === 'CORRECTION'
                          ? <span className="txn-type-badge correction"><FaWrench /> CORR</span>
                          : <span className={`txn-type-badge ${tx.type === 'OUT' ? 'out' : 'in'}`}>
                              {tx.type === 'OUT' ? <FaArrowUp /> : <FaArrowDown />} {tx.type}
                            </span>
                        }
                      </td>
                      <td>
                        {tx.type === 'CORRECTION'
                          ? <span style={{ color: '#8896a5', fontStyle: 'italic' }}>{tx.notes || 'Manual correction'}</span>
                          : tx.type === 'OUT' ? (tx.aircraft || '—') : (tx.vendor || '—')}
                      </td>
                      <td>{tx.fueler || '—'}</td>
                      <td><strong>{fmtNum(tx.amount)} {tx.amount_type}</strong></td>
                      <td style={{ color: '#8896a5', fontSize: 12 }}>{fmtTs(tx.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pages > 1 && (
              <div className="fl-pagination">
                <button className="btn btn-sm btn-secondary" disabled={page === 1} onClick={() => handlePage(page - 1)}>‹ Prev</button>
                <span className="fl-page-info">Page {page} of {pages}</span>
                <button className="btn btn-sm btn-secondary" disabled={page >= pages} onClick={() => handlePage(page + 1)}>Next ›</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function FuelLevels() {
  const apiCall = useApiCall();
  const location = useLocation();
  const [tankers, setTankers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTanker, setSelectedTanker] = useState(location.state?.tanker ?? null);
  const { openModal } = useModal();
  const { success, showToast } = useToast();

  // When the user navigates to this page without a tanker in state (e.g. sidebar click),
  // clear the detail view so the grid is shown.
  useEffect(() => {
    if (!location.state?.tanker) setSelectedTanker(null);
  }, [location]);

  const [refreshLocked, setRefreshLocked] = useState(false);

  const loadTankers = () => {
    setLoading(true);
    apiCall('admin/tankers', 'POST').then(res => {
      if (res?.success) setTankers(res.data.tankers || []);
      setLoading(false);
    });
  };

  const handleRefreshGrid = () => {
    if (refreshLocked) return;
    setRefreshLocked(true);
    loadTankers();
    setTimeout(() => setRefreshLocked(false), 5000);
  };

  useEffect(() => { loadTankers(); }, []); // eslint-disable-line

  const handleSave = (tankerId, newLevel, reason) => {
    apiCall('admin/tankers/fuel-level/correct', 'POST', { tanker_id: tankerId, amount: newLevel, reason }).then(res => {
      if (res?.success) {
        success(`Fuel level updated${reason ? ` - "${reason}"` : ''}`);
        loadTankers();
        // If we're in detail view, refresh it by re-mounting via key change
        if (selectedTanker?.id === tankerId) {
          setSelectedTanker(prev => ({ ...prev, _refresh: Date.now() }));
        }
      } else {
        showToast(res?.message || 'Failed to update fuel level', 'error');
      }
    });
  };

  const handleForceUnlock = (tanker) => {
    openModal(
      `Force unlock — ${tanker.name}`,
      <div className="delete-confirmation">
        <p>Mark <strong>{tanker.name}</strong> as <strong>not in use</strong>?</p>
        <p className="warning-text">Only do this if the fueling app crashed or was closed mid-session.</p>
      </div>,
      'sm',
      {
        footer: (closeModal) => (
          <>
            <button className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
            <button className="btn btn-danger" onClick={() => {
              apiCall('admin/tankers/force-unlock', 'POST', { tanker_id: tanker.id }).then(res => {
                if (res?.success) {
                  success(`${tanker.name} unlocked`);
                  loadTankers();
                  if (selectedTanker?.id === tanker.id) setSelectedTanker(prev => ({ ...prev, in_use: 0, _refresh: Date.now() }));
                } else {
                  showToast(res?.message || 'Failed to unlock tanker', 'error');
                }
              });
              closeModal();
            }}><FaUnlock /> Force unlock</button>
          </>
        )
      }
    );
  };

  const openCorrect = (tanker) => {
    openModal(
      `Correct fuel level - ${tanker.name}`,
      <EditFuelForm tanker={tanker} onSave={handleSave} />,
      'sm',
      {
        closeOnBackdrop: false,
        footer: (closeModal) => (
          <>
            <button type="button" className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
            <button type="submit" form="fuel-form" className="btn btn-primary"><FaSave /> Save correction</button>
          </>
        )
      }
    );
  };

  // ── Detail view ──────────────────────────────────────────────────────────────
  if (selectedTanker) {
    return (
      <div className="page-container">
        <TankerDetail
          key={selectedTanker.id + (selectedTanker._refresh ?? '')}
          tanker={selectedTanker}
          onBack={() => { setSelectedTanker(null); loadTankers(); }}
          onCorrect={openCorrect}
          onForceUnlock={handleForceUnlock}
        />
      </div>
    );
  }

  // ── Grid view ────────────────────────────────────────────────────────────────
  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Fuel Levels</h1>
          <p className="page-subtitle">View and manually correct tanker fuel levels</p>
        </div>
        <button className="btn btn-secondary" onClick={handleRefreshGrid} disabled={refreshLocked}>
          <FaSync className={refreshLocked ? 'icon-spin' : ''} /> {refreshLocked ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading...</div>
      ) : (
        <div className="fl-grid">
          {tankers.map(t => {
            const pct = t.level_pct;
            const barColor = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';
            return (
              <div
                key={t.id}
                className="fl-card fl-card-clickable"
                onClick={() => setSelectedTanker(t)}
              >
                <div className="fl-card-header">
                  <div className="fl-title">
                    <FaTruck className="fl-icon" />
                    <span>{t.name}</span>
                    <span className={`tanker-status-badge ${t.in_use ? 'in-use' : 'available'}`}>
                      {t.in_use ? 'In Use' : 'Available'}
                    </span>
                  </div>
                  <div className="fl-card-actions">
                    {t.in_use ? (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={e => { e.stopPropagation(); handleForceUnlock(t); }}
                      >
                        <FaUnlock /> Unlock
                      </button>
                    ) : null}
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={e => { e.stopPropagation(); openCorrect(t); }}
                    >
                      <FaEdit /> Correct
                    </button>
                  </div>
                </div>

                <div className="fl-fuel-type">
                  <FaGasPump /> {t.fuelType}
                </div>

                <div className="fl-bar-section">
                  <div className="fuel-bar-track large">
                    <div className="fuel-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                  </div>
                  <div className="fl-bar-labels">
                    <span style={{ color: barColor, fontWeight: 700, fontSize: 22 }}>{pct}%</span>
                    <span className="fl-gal">{t.amount} / {t.amount_cap} {t.amount_type}</span>
                  </div>
                </div>

                {pct < 20 && (
                  <div className="fl-warning">
                    <FaExclamationTriangle /> Low fuel - refill required
                  </div>
                )}
                {t.bowser_check === 'fail' && (
                  <div className="fl-warning fl-bowser-fail">
                    <FaTimesCircle /> Bowser check failed
                  </div>
                )}
                {t.bowser_check === 'not_checked' && (
                  <div className="fl-warning fl-bowser-none">
                    <FaClipboardCheck /> No bowser check today
                  </div>
                )}

                <div className="fl-card-footer">
                  Click to view details & history →
                </div>
              </div>
            );
          })}
          {tankers.length === 0 && <div className="empty-state">No tankers found</div>}
        </div>
      )}
    </div>
  );
}

